import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PageContainer from '../../components/layout/PageContainer/PageContainer';
import SearchForm from '../../components/search/SearchForm/SearchForm';
import { PaymentFailedAlert } from '../../components/common/PaymentFailedAlert/PaymentFailedAlert';
import PostSearchSurveyModal from '../../components/surveys/PostSearchSurveyModal';
import { subscriptionsApi } from '../../api/subscriptions';
import { useAuth } from '../../hooks/useAuth';
import { storeAuthData } from '../../utils/auth';
import { setPostSearchSurveyCompleted } from '../../store/slices/authSlice';

const SearchCreation: React.FC = () => {
  const { isAuthenticated, user, token, fetchCurrentUser } = useAuth();
  const dispatch = useDispatch();
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('');
  const [nextPaymentAttempt, setNextPaymentAttempt] = useState<number | null>(null);

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (!isAuthenticated) return;

      try {
        const response = await subscriptionsApi.getCurrentSubscription();
        setSubscriptionStatus(response.subscription?.status ?? '');
        setNextPaymentAttempt(response.subscription?.next_payment_attempt || null);
      } catch (error) {
        console.error('Failed to fetch subscription status:', error);
      }
    };

    fetchSubscriptionStatus();
  }, [isAuthenticated]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const completedSearches = user?.completed_searches_count ?? 0;

  // The `< 2` guard prevents double-mount: users with >= 2 searches are served
  // the survey globally by App.tsx. Without it, both mounts would render simultaneously.
  const showSurvey =
    !!user &&
    user.onboarding_completed &&
    !user.post_search_survey_completed &&
    completedSearches >= 1 &&
    completedSearches < 2 &&
    !!user.exported_results;

  return (
    <PageContainer title="New Search">
      <PaymentFailedAlert
        subscriptionStatus={subscriptionStatus}
        nextPaymentAttempt={nextPaymentAttempt}
      />
      <SearchForm />
      {showSurvey && (
        <PostSearchSurveyModal
          username={user!.username}
          completedSearchesCount={completedSearches}
          onComplete={() => {
            dispatch(setPostSearchSurveyCompleted());
            if (token) {
              storeAuthData(token, { ...user!, post_search_survey_completed: true });
            }
          }}
        />
      )}
    </PageContainer>
  );
};

export default SearchCreation;
